import { apiGet, apiPatch, apiPost, apiDelete } from "./utils";
import { toast } from "sonner";
import type {
  ScoringConfigFormValues,
  ConversationActionScore,
  ReviewThreshold,
  NotificationFrequency,
  Weekday,
} from "../modules/scoring/types";
import { DEFAULT_SCORING_FORM_VALUES } from "../modules/scoring/schema";

const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

type AgentActionRecord = {
  id: string;
  actionKey: string;
  points: number;
};

type BandRecord = {
  id: string;
  lowerBound: number;
  score: number;
};

type WorkspaceRecord = {
  messagingActive: boolean;
  messagingDays: Record<string, boolean>;
  pointsCalculationTime: string | null;
  timezone: string | null;
  agentActions: AgentActionRecord[];
  qualityBands: BandRecord[];
};

/**
 * Converts form frequency/day values into the messaging_days hash
 * the workspace API expects.
 *
 * - "daily"  → all days true
 * - "weekly" → only the selected day true
 */
const buildMessagingDays = (
  frequency: NotificationFrequency,
  day: Weekday,
): Record<string, boolean> => {
  if (frequency === "daily") {
    return Object.fromEntries(WEEKDAYS.map((d) => [d, true]));
  }
  return Object.fromEntries(WEEKDAYS.map((d) => [d, d === day]));
};

const deriveFrequencyFromDays = (
  days: Record<string, boolean> | null,
): { frequency: NotificationFrequency; day: Weekday } => {
  if (!days) return { frequency: "weekly", day: "monday" };

  const activeDays = WEEKDAYS.filter((d) => days[d]);
  if (activeDays.length === 7) return { frequency: "daily", day: "monday" };

  const singleDay = activeDays[0] as Weekday | undefined;
  return { frequency: "weekly", day: singleDay ?? "monday" };
};

const mapActionsToForm = (actions: AgentActionRecord[]): ConversationActionScore[] =>
  actions.map((a) => ({
    action: a.actionKey as ConversationActionScore["action"],
    points: a.points,
  }));

const mapBandsToForm = (bands: BandRecord[]): ReviewThreshold[] =>
  bands
    .slice()
    .sort((a, b) => a.lowerBound - b.lowerBound)
    .map((b) => ({ percent: b.lowerBound, points: b.score }));

/**
 * Diff agent_actions by actionKey: create missing, update changed, delete removed.
 */
const syncAgentActions = async (
  existing: AgentActionRecord[],
  desired: ConversationActionScore[],
): Promise<void> => {
  const existingByKey = new Map(existing.map((a) => [a.actionKey, a]));
  const desiredByKey = new Map(desired.map((a) => [a.action, a]));

  const toCreate = desired.filter((a) => !existingByKey.has(a.action));
  const toUpdate = desired.filter((a) => {
    const record = existingByKey.get(a.action);
    return record && record.points !== a.points;
  });
  const toDelete = existing.filter((a) => !desiredByKey.has(a.actionKey as ConversationActionScore["action"]));

  for (const action of toDelete) {
    await apiDelete(`/api/v1/agent_actions/${action.id}`);
  }

  for (const action of toCreate) {
    await apiPost("/api/v1/agent_actions", {
      actionKey: action.action,
      points: action.points,
    });
  }

  for (const action of toUpdate) {
    const record = existingByKey.get(action.action)!;
    await apiPatch(`/api/v1/agent_actions/${record.id}`, {
      points: action.points,
    });
  }
};

/**
 * Replace all quality_bands: create desired first, then delete existing.
 * Creating first ensures a failure leaves the old config intact rather than
 * wiping records before the new ones exist.
 */
const syncBands = async (
  existing: BandRecord[],
  desired: ReviewThreshold[],
): Promise<void> => {
  for (const threshold of desired) {
    await apiPost("/api/v1/quality_bands", {
      lowerBound: threshold.percent,
      score: threshold.points,
    });
  }

  for (const band of existing) {
    await apiDelete(`/api/v1/quality_bands/${band.id}`);
  }
};

export const scoringApi = {
  load: async (): Promise<ScoringConfigFormValues> => {
    const workspace = await apiGet<WorkspaceRecord>(
      "/api/v1/workspace?include=agent_actions,quality_bands",
    );

    const { frequency, day } = deriveFrequencyFromDays(workspace.messagingDays);
    const actions = mapActionsToForm(workspace.agentActions ?? []);
    const thresholds = mapBandsToForm(workspace.qualityBands ?? []);

    return {
      notificationFrequency: frequency,
      messagingDay: day,
      notificationTime: workspace.pointsCalculationTime
        ? new Date(workspace.pointsCalculationTime).toISOString().slice(11, 16)
        : DEFAULT_SCORING_FORM_VALUES.notificationTime,
      notificationTimezone: workspace.timezone ?? DEFAULT_SCORING_FORM_VALUES.notificationTimezone,
      conversationActions: actions,
      reviewThresholds: thresholds,
    };
  },

  save: async (values: ScoringConfigFormValues): Promise<void> => {
    const messagingDays = buildMessagingDays(
      values.notificationFrequency,
      values.messagingDay,
    );

    try {
      const workspaceResponse = await apiPatch<WorkspaceRecord>(
        "/api/v1/workspace?include=agent_actions,quality_bands",
        {
          messagingDays,
          pointsCalculationTime: values.notificationTime,
          timezone: values.notificationTimezone,
        },
      );

      await syncAgentActions(
        workspaceResponse.agentActions ?? [],
        values.conversationActions,
      );

      await syncBands(
        workspaceResponse.qualityBands ?? [],
        values.reviewThresholds,
      );
    } catch (error) {
      toast.error("Failed to save scoring configuration. Please try again.");
      throw error;
    }
  },
};
